import { useCallback } from 'react';
import type { BooleanSchema, MixedSchema } from 'yup';
import * as Yup from 'yup';
import { ArraySchema, DateSchema, NumberSchema, Schema, StringSchema, ValidationError } from 'yup';

import type { Field } from '../../../types';

import { isEmpty, isNil, isNumeric, Regexes } from '../../../utils';

import type { LocaleOption } from '../../Intl';
import { useLocale } from '../../Intl';
import { locale as intl } from 'app/components/Intl/IntlWrapper';

export const getMonthsOptions = (locale: LocaleOption) => {
  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(month => ({
    element: locale.locale.localize?.month(month),
    value: `${month}`,
  }));
};

const mapOptions = (options: Array<any>) =>
  options.map((option: any) => {
    if (typeof option === 'string') {
      return option;
    }
    return option?.value;
  });

const mapErrorHelperText = (locale: LocaleOption, options: Array<any>) =>
  options
    .map((option: any) => {
      if (typeof option === 'string') {
        return option;
      }

      const { element, value } = option;
      return element ?? locale.messages[value] ?? value;
    })
    .join(', ');

const buildMultiSelect = (locale: LocaleOption, field: Field) => {
  const selectSchema = Yup.array();

  if (field.required) {
    return selectSchema.required();
  }

  return selectSchema;
};

const buildSelect = (locale: LocaleOption, field: Field) => {
  const selectSchema = field.options?.multiple ? Yup.array() : Yup.mixed();

  if (field.options?.freeSolo) {
    return selectSchema;
  }

  if (field.options?.multiple) {
    return selectSchema;
  }

  let optionsArray = field.items || [];

  if (isEmpty(optionsArray)) {
    return selectSchema;
  }

  optionsArray = optionsArray.map(option => ({
    default: option.default ?? false,
    value: option.value,
    element: intl.messages[option.element] ?? option.element,
  }));

  const errorHelperText = mapErrorHelperText(locale, optionsArray);
  let possibleValues = mapOptions(optionsArray);

  if (selectSchema instanceof ArraySchema) {
    return selectSchema.oneOf(
      possibleValues,
      ValidationError.formatError(locale.validationMessages.mixed?.oneOf, { values: errorHelperText }),
    );
  }

  // @see https://github.com/jquense/yup/issues/104
  if (!field.required) {
    possibleValues = ['', null, undefined, ...possibleValues];
  }
  return selectSchema.oneOf(
    possibleValues,
    ValidationError.formatError(locale.validationMessages.mixed?.oneOf, { values: errorHelperText }),
  );
};

const getBaseSchema = (
  locale: LocaleOption,
  field: Field,
  defaultType: Field['type'],
):
  | DateSchema
  | NumberSchema
  | StringSchema
  | BooleanSchema
  | MixedSchema
  | Schema
  | ReturnType<typeof buildSelect>
  | ReturnType<typeof buildMultiSelect> => {
  switch (field.type ?? defaultType) {
    case 'multi-select':
      return buildMultiSelect(locale, field);
    case 'list':
    case 'select':
    case 'autocomplete':
      return buildSelect(locale, field);
    case 'month': {
      return buildSelect(locale, { ...field, items: getMonthsOptions(locale) });
    }
    case 'table':
    case 'array':
      return Yup.array();
    case 'date':
      return Yup.string()
        .transform(current => (current === '' ? null : current))
        .test('valid-date', 'Invalid date', value => {
          if (!value) {
            return true;
          }

          // Check if the serialized date contains invalid year patterns
          if (value.includes('NaN') || value === 'Invalid Date') {
            return false;
          }

          const [year] = value.split('-');

          // Reject years that start with 0 (like 0098, 0052)
          if (year && year.startsWith('0') && year.length > 1) {
            return false;
          }

          // Reject unreasonable years
          const yearNum = parseInt(year, 10);
          const currentYear = new Date().getFullYear();
          const minValidYear = 1900;
          const maxValidYear = currentYear + 50;

          return yearNum >= minValidYear && yearNum <= maxValidYear;
        })
        .test('valid-calendar-date', 'Invalid calendar date', value => {
          if (!value) {
            return true;
          }

          // Handle null or NaN values from parseDate
          if (value.includes('NaN')) {
            return false;
          }

          const datePart = value.split('T')[0];
          const [year, month, day] = datePart.split('-').map(Number);
          const date = new Date(year, month - 1, day);
          return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
        });
    case 'checkbox':
    case 'boolean':
      return Yup.boolean();
    case 'tel':
    case 'number':
    case 'float':
      return Yup.number();
    case 'email':
      return Yup.string().ensure().trim().max(255).email();
    case 'url':
      return Yup.string().ensure().trim().max(255).url();
    case 'password':
      return Yup.string().ensure().trim().max(255);
    case 'text':
    case 'string':
      return Yup.string()
        .ensure()
        .test('no-edge-whitespace', 'No whitespaces are allowed at the beginning or end', value => {
          if (value == null || value === '') return true;
          return value === value.trim();
        })
        .test('no-emojis', 'No emojis allowed', val => {
          if (!val) return true;
          const emojiRegex =
            /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/;
          return !emojiRegex.test(val);
        });
    default:
      return Yup.mixed();
  }
};

const buildSchema = (locale: LocaleOption, field: Field, defaultType: Field['type']): Schema => {
  if (field.loading) {
    return Yup.mixed();
  }

  if (field.schema instanceof Schema) {
    return field.schema;
  }

  let schema = getBaseSchema(locale, field, defaultType);

  if (field.required) {
    schema = schema.required();
    if (isNil(field.options?.min) && schema instanceof ArraySchema) {
      schema = schema.min(1);
    }
  } else {
    schema = schema.nullable().optional();
  }

  if (!isNil(field.options?.min) && isNumeric(field.options?.min)) {
    if (schema instanceof NumberSchema) {
      schema = schema.min(parseFloat(`${field.options?.min}`));
    } else if (schema instanceof StringSchema || schema instanceof ArraySchema) {
      schema = schema.min(parseInt(`${field.options?.min}`));
    } else if (schema instanceof DateSchema) {
      schema = schema.min(field.options?.min);
    }
  }

  if (!isNil(field.options?.max) && isNumeric(field.options?.max)) {
    if (schema instanceof NumberSchema) {
      schema = schema.max(parseFloat(`${field.options?.max}`));
    } else if (schema instanceof StringSchema || schema instanceof ArraySchema) {
      schema = schema.max(parseInt(`${field.options?.max}`));
    } else if (schema instanceof DateSchema) {
      schema = schema.max(field.options?.max);
    }
  }

  if (!isNil(field.options?.noTrimWhitespaces) && field.options?.noTrimWhitespaces) {
    if (schema instanceof StringSchema) {
      schema = schema.trim().strict().max(255);
    }
  }

  if (!isNil(field.options?.noWhitespaces) && field.options?.noWhitespaces) {
    if (schema instanceof StringSchema) {
      schema = schema.test('no-whitespaces', 'Whitespaces are not allowed', val => {
        if (!val) return true;
        return !/\s/.test(val);
      });
    }
  }

  if (!isNil(field.options?.noEmojis) && field.options?.noEmojis) {
    if (schema instanceof StringSchema || schema instanceof NumberSchema) {
      schema = schema.test('no-emojis', 'No emojis allowed', val => {
        if (!val) return true;
        const emojiRegex =
          /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/;
        return !emojiRegex.test(val);
      });
    }
  }

  if (!isNil(field.options?.match) && schema instanceof StringSchema) {
    if (field.options?.match === 'hostOrIP') {
      schema = schema.matches(Regexes.HostOrIPExact);
    }
  }

  if (field.schema instanceof Function) {
    return field.schema(schema);
  }

  return schema;
};

export const useFieldValidator = (
  defaultType: Field['type'],
): ((value: any, field: Field) => Promise<string | undefined>) => {
  const locale = useLocale();
  return useCallback(
    (value, field) => {
      const schema = buildSchema(locale, field, defaultType);
      return schema
        .validate(value)
        .then(() => {
          return undefined;
        })
        .catch((error: Error) => {
          if (ValidationError.isError(error)) {
            if (error.inner) {
              if (error.inner.length === 0) {
                return error.message;
              }
              return error.inner[0].message;
            }
          }
          throw error;
        });
    },
    [defaultType, locale],
  );
};
